import React, { useState } from 'react';
import { getGroundingExercise, formatCrisisResources } from '../utils/crisisDetection';

export default function CrisisAlert({ level, response, onContinue, onGroundingComplete }) {
  const [showGrounding, setShowGrounding] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const grounding = getGroundingExercise();
  const resources = response.resources ? formatCrisisResources(response.resources) : [];

  const handleGroundingStart = () => {
    setShowGrounding(true);
    setCurrentStep(0);
  };

  const handleNextStep = () => {
    if (currentStep < grounding.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowGrounding(false);
      if (onGroundingComplete) onGroundingComplete();
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Grounding Exercise View
  if (showGrounding) {
    const step = grounding.steps[currentStep];
    const isLastStep = currentStep === grounding.steps.length - 1;

    return (
      <div style={styles.overlay}>
        <div style={styles.groundingCard}>
          <h2 style={styles.groundingTitle}>{grounding.title}</h2>
          
          {currentStep === 0 && (
            <p style={styles.groundingIntro}>{grounding.intro}</p>
          )}

          <div style={styles.stepContainer}>
            <div style={styles.stepNumber}>{step.number}</div>
            <h3 style={styles.stepInstruction}>{step.instruction}</h3>
            <p style={styles.stepExample}>For example: {step.example}</p>
          </div>

          <div style={styles.stepProgress}>
            Step {currentStep + 1} of {grounding.steps.length}
          </div>

          <div style={styles.buttonContainer}>
            {currentStep > 0 && (
              <button 
                style={styles.secondaryButton}
                onClick={handlePreviousStep}
              >
                ← Previous
              </button>
            )}
            
            <button 
              style={styles.primaryButton}
              onClick={handleNextStep}
            >
              {isLastStep ? 'Complete' : 'Next →'}
            </button>
          </div>

          {isLastStep && (
            <p style={styles.groundingClosing}>{grounding.closing}</p>
          )}
        </div>
      </div>
    );
  }

  // Crisis Alert View
  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        borderTop: level === 'tier2' ? '6px solid #dc2626' : '6px solid #f59e0b'
      }}>
        {/* Icon */}
        <div style={styles.iconContainer}>
          <span style={{
            ...styles.icon,
            color: level === 'tier2' ? '#dc2626' : '#f59e0b'
          }}>
            {level === 'tier2' ? '🆘' : '⚠️'}
          </span>
        </div>

        {/* Title & Message */}
        <h2 style={styles.title}>{response.title}</h2>
        <p style={styles.message}>{response.message}</p>

        {/* Tier 2: Crisis Resources */}
        {level === 'tier2' && resources.length > 0 && (
          <div style={styles.resourcesContainer}>
            <h3 style={styles.resourcesTitle}>24/7 Crisis Support</h3>
            {resources.map((resource, idx) => (
              <div key={idx} style={styles.resourceCard}>
                <div style={styles.resourceHeader}>
                  <strong>{resource.name}</strong>
                  {resource.region && (
                    <span style={styles.regionBadge}>{resource.region}</span>
                  )}
                </div>
                <a 
                  href={resource.link || `tel:${resource.phone}`}
                  style={styles.resourceLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resource.phone || 'Visit Website'}
                </a>
                {resource.description && (
                  <p style={styles.resourceDescription}>{resource.description}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={styles.actionsContainer}>
          <button
            style={styles.groundingButton}
            onClick={handleGroundingStart}
          >
            🌬️ Try Grounding Exercise
          </button>

          {level === 'tier1' && (
            <button
              style={styles.continueButton}
              onClick={onContinue}
            >
              I'm Ready to Continue
            </button>
          )}

          {level === 'tier2' && (
            <div style={styles.tier2Notice}>
              <p style={styles.noticeText}>
                Please reach out for support before continuing. Your safety matters most.
              </p>
            </div>
          )}
        </div>

        {/* Additional Guidance */}
        {response.actions && response.actions.length > 0 && (
          <div style={styles.guidanceContainer}>
            <h4 style={styles.guidanceTitle}>What happens next:</h4>
            <ul style={styles.guidanceList}>
              {response.actions.map((action, idx) => (
                <li key={idx} style={styles.guidanceItem}>{action}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    animation: 'slideUp 0.4s ease-out'
  },
  iconContainer: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  icon: {
    fontSize: '64px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '15px',
    textAlign: 'center'
  },
  message: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#4b5563',
    marginBottom: '30px',
    textAlign: 'center'
  },
  resourcesContainer: {
    background: '#fef2f2',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px'
  },
  resourcesTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#dc2626',
    marginBottom: '15px'
  },
  resourceCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '12px',
    border: '2px solid #fee2e2'
  },
  resourceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  regionBadge: {
    background: '#dc2626',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  resourceLink: {
    display: 'block',
    fontSize: '20px',
    fontWeight: '700',
    color: '#dc2626',
    textDecoration: 'none',
    marginBottom: '8px'
  },
  resourceDescription: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px'
  },
  groundingButton: {
    padding: '16px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  continueButton: {
    padding: '14px',
    background: 'transparent',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  tier2Notice: {
    background: '#fef2f2',
    borderRadius: '8px',
    padding: '16px',
    border: '2px solid #fee2e2'
  },
  noticeText: {
    fontSize: '14px',
    color: '#dc2626',
    margin: 0,
    textAlign: 'center',
    fontWeight: '600'
  },
  guidanceContainer: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '16px'
  },
  guidanceTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    marginBottom: '12px'
  },
  guidanceList: {
    margin: 0,
    paddingLeft: '20px'
  },
  guidanceItem: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '8px'
  },
  // Grounding Exercise Styles
  groundingCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
  },
  groundingTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#059669',
    marginBottom: '15px',
    textAlign: 'center'
  },
  groundingIntro: {
    fontSize: '15px',
    color: '#6b7280',
    marginBottom: '30px',
    textAlign: 'center',
    lineHeight: '1.6'
  },
  stepContainer: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  stepNumber: {
    fontSize: '72px',
    fontWeight: '700',
    color: '#10b981',
    marginBottom: '20px'
  },
  stepInstruction: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '15px'
  },
  stepExample: {
    fontSize: '14px',
    color: '#9ca3af',
    fontStyle: 'italic'
  },
  stepProgress: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '20px'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center'
  },
  primaryButton: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1
  },
  secondaryButton: {
    padding: '14px 32px',
    background: 'transparent',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1
  },
  groundingClosing: {
    fontSize: '14px',
    color: '#059669',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: '20px'
  }
};
